import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import IgnoreIcon from '@material-ui/icons/VisibilityOff';
import ReplayIcon from '@material-ui/icons/Replay';
import { IssueStatus } from '../../types';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
  },
  commentField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  statusIcon: {
    marginRight: theme.spacing(1),
  },
}));

interface StatusUpdateDialogProps {
  open: boolean;
  status: IssueStatus | null;
  isUpdating: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}

export const StatusUpdateDialog: React.FC<StatusUpdateDialogProps> = ({
  open,
  status,
  isUpdating,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const MAX_LENGTH = 10000;

  useEffect(() => {
    if (!open) {
      setComment('');
      setError('');
    }
  }, [open]);

  const handleSubmit = () => {
    if (!comment.trim()) {
      setError('Comment is required to update issue status');
      return;
    }

    if (comment.length > MAX_LENGTH) {
      setError(`Comment exceeds maximum length of ${MAX_LENGTH.toLocaleString()} characters`);
      return;
    }

    onConfirm(comment.trim());
  };

  const handleClose = () => {
    setComment('');
    setError('');
    onClose();
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Only update if within limit
    if (newValue.length <= MAX_LENGTH) {
      setComment(newValue);
      if (error) setError('');
    }
  };

  const getHelperText = () => {
    if (error) return error;
    
    const remaining = MAX_LENGTH - comment.length;
    const characterCount = `${comment.length.toLocaleString()}/${MAX_LENGTH.toLocaleString()} characters`;
    
    if (remaining === 0) {
      return `${characterCount} - Character limit reached`;
    } else if (remaining < 100) {
      return `${characterCount} - ${remaining} remaining`;
    } else {
      return characterCount;
    }
  };

  const getStatusInfo = () => {
    switch (status) {
      case IssueStatus.RESOLVED:
        return {
          title: 'Resolve Issue',
          action: 'Resolve',
          icon: <CheckIcon className={classes.statusIcon} />,
          color: '#28a745',
        };
      case IssueStatus.IGNORED:
        return {
          title: 'Ignore Issue',
          action: 'Ignore',
          icon: <IgnoreIcon className={classes.statusIcon} />,
          color: '#6c757d',
        };
      case IssueStatus.UNRESOLVED:
        return {
          title: 'Reopen Issue',
          action: 'Reopen',
          icon: <ReplayIcon className={classes.statusIcon} />,
          color: '#dc3545',
        };
      default:
        return {
          title: 'Update Issue',
          action: 'Update',
          icon: null,
          color: '#007bff',
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle 
        className={classes.dialogTitle} 
        style={{ position: 'relative', paddingRight: '48px' }}
      >
        <Box display="flex" alignItems="center" mb={0.5}>
          {statusInfo.icon}
          <Typography variant="h6" style={{ marginLeft: 8 }}>
            {statusInfo.title}
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="medium"
          aria-label="close"
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Please provide a reason for this status change. This comment will be added to the issue history.
        </Typography>

        <TextField
          className={classes.commentField}
          label="Reason for status change*"
          placeholder="Explain why you are changing the status..."
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={comment}
          onChange={handleCommentChange}
          error={!!error}
          helperText={getHelperText()}
          disabled={isUpdating}
          inputProps={{
            maxLength: MAX_LENGTH,
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isUpdating || !comment.trim()}
          style={{ 
            backgroundColor: (isUpdating || !comment.trim()) ? undefined : statusInfo.color, 
            color: (isUpdating || !comment.trim()) ? undefined : 'white'
          }}
          className={classes.actionButton}
        >
          {isUpdating ? 'Updating...' : `${statusInfo.action} Issue`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};